import React, { useCallback, useEffect } from 'react';
import Modal, { ModalBody } from '@strava/ui/Modal';
import {
  useStravaAnalytics,
  useStravaTranslations
} from '@strava/container-context';
import AccountAuthButton from '../AccountAuthButton';
import OrDivider from '../OrDivider';
import EmailRegistrationForm, {
  EmailRegistrationFormProps,
  EMAIL_REGISTRATION_FORM_ALERT_ID,
  AnalyticsFieldsTypes
} from '../EmailRegistrationForm';
import DisclaimerText from '../DisclaimerText';
import { APPLE, FACEBOOK, GOOGLE } from '../constants';
import { AlertContainer, AlertProvider } from '@strava/ui/Alert';
import Button, { LinkButton } from '@strava/ui/Button';
import VisuallyHidden from '@strava/ui/VisuallyHidden';
import ActionsCancelNormalSmall from '@strava/icons/ActionsCancelNormalSmall';
import { color } from '@strava/ui-tokens/js';
import { useSignUpModalContext } from './SignUpModalContext';
import styles from './SignUpModal.module.scss';

// using `require` here instead of import to avoid adding `declare module '*.svg'`
// everywhere the `@strava/authentication-ui` package is imported
// eslint-disable-next-line @typescript-eslint/no-var-requires
const echelon = require('./assets/strava-echelon.svg');

export type SignUpModalProps = {
  authorizationUrls: {
    email: string;
    facebook: string;
    google: string;
  };
  appleState: string;
  category: string;
  className?: string;
} & EmailRegistrationFormProps;

const i18nPrefix = 'sign_up_modal';

const SignUpModal = ({
  className,
  authorizationUrls,
  category,
  ...props
}: SignUpModalProps) => {
  const { track: _track } = useStravaAnalytics();
  const { t } = useStravaTranslations();

  const { isSignUpModalOpen, dismissSignUpModal, variant, copyParams } =
    useSignUpModalContext();

  // If we need to add `_v2` strings for the variants, we'd need to map them to the proper variant.
  // The variant is used for more than just copy, so we could end up with `follow_athlete` as a variant
  // but want to use `follow_athlete_v2` for the copy.

  const track = useCallback(
    (fields: AnalyticsFieldsTypes) => {
      _track({
        category,
        page: 'signup_modal',
        ...fields,
        properties: {
          copy_variant: variant,
          ...fields.properties
        }
      });
    },
    [_track, category, variant]
  );

  const handleExtAuthBtnClick = (
    option: typeof FACEBOOK | typeof GOOGLE | typeof APPLE
  ) => {
    track({ action: 'click', element: `${option}_signup` });

    // TODO ADIEL: auth url redirect, post-auth actions
    return option;
  };

  useEffect(() => {
    track({ action: 'screen_enter' });
  }, [track]);

  return (
    <AlertProvider>
      <Modal
        isOpen={isSignUpModalOpen}
        onDismiss={dismissSignUpModal}
        aria-labelledby="sign-up-modal-header"
        className={styles.signUpModal}
      >
        <ModalBody className={styles.modalBody}>
          <div className={styles.headerButtons}>
            <LinkButton
              href={'/login'}
              variant="text"
              className={styles.loginButton}
              onClick={() => track({ action: 'click', element: 'login' })}
            >
              {t(`${i18nPrefix}.log_in`)}
            </LinkButton>
            <Button
              onClick={() => {
                track({ action: 'click', element: 'close_modal' });
                dismissSignUpModal();
              }}
              type="button"
              variant="icon"
              className={styles.closeButton}
              title={t(`${i18nPrefix}.close`)}
            >
              <VisuallyHidden>{t(`${i18nPrefix}.close`)}</VisuallyHidden>
              <ActionsCancelNormalSmall fill={color.colorCoreO3} />
            </Button>
          </div>

          <div className={styles.headerText}>
            <img src={echelon} alt="" width="30px" height="30px" />
            <h2 id="sign-up-modal-header">
              {t(`${i18nPrefix}.${variant}.title`, copyParams[variant])}
            </h2>
            <p>{t(`${i18nPrefix}.${variant}.subtitle`)}</p>
          </div>

          <AlertContainer containerId={EMAIL_REGISTRATION_FORM_ALERT_ID} />

          <AccountAuthButton
            variant="google"
            onClick={() => handleExtAuthBtnClick(GOOGLE)}
            data-testid="google_auth_btn"
          >
            {t(`${i18nPrefix}.signup_ctas.google_cta`)}
          </AccountAuthButton>

          <AccountAuthButton
            variant="apple"
            onClick={() => handleExtAuthBtnClick(APPLE)}
            data-testid="apple_auth_btn"
          >
            {t(`${i18nPrefix}.signup_ctas.apple_cta`)}
          </AccountAuthButton>

          <AccountAuthButton
            variant="facebook"
            onClick={() => handleExtAuthBtnClick(FACEBOOK)}
            data-testid="facebook_auth_btn"
          >
            {t(`${i18nPrefix}.signup_ctas.facebook_cta`)}
          </AccountAuthButton>

          <OrDivider text={t(`${i18nPrefix}.or`)} theme="light" />

          <EmailRegistrationForm
            authorizationUrl={authorizationUrls.email}
            track={track}
            idPrefix="sign-up-modal"
            theme="light"
            alertConfig={{ className: styles.alert, contentPosition: 'center' }}
            {...props}
          />

          <DisclaimerText className={styles.disclaimerText} />
        </ModalBody>
      </Modal>
    </AlertProvider>
  );
};

export default SignUpModal;
